import React, { useCallback, useEffect, useState } from 'react';

import Button, { LoadingButton } from '@atlaskit/button';
import ProgressBar from '@atlaskit/progress-bar';
import SectionMessage from '@atlaskit/section-message';
import { closeDialog } from 'src/services/jira';
import { setProjectLevelSidebarVisibilityWithRetry, useAllRules } from 'src/services/rules';
import { setSidebarVisibleByDefault } from 'src/services/sidebarVisibility';

import { trackEvent } from 'src/lib/analytics';
import jira from 'src/lib/api/jira';

import ModalButtons from 'src/views/components/ModalButtons';
import ModalContainer from 'src/views/components/ModalContainer';

const ShowProgress = ({ progress, toProcess = 0, processed = 0, projectsFailedToProcess = [] } = {}) => {
  return (
    <div>
      Processing ({processed + 1 } of {toProcess + processed})
      <ProgressBar value={progress}></ProgressBar>
      {projectsFailedToProcess.length > 0 && <span><strong>{projectsFailedToProcess.join(', ')}</strong> failed to process.</span>}
    </div>
  );
};

const DataPresentation = ({ uniqueKeys = [], browseBaseUrl } = {}) => {
  const [projectsProcessed, setProjectsProcessed] = useState([]);
  const [projectsToProcess, setProjectsToProcess] = useState(uniqueKeys);
  const [projectsFailedToProcess, setProjectsFailedToProcess] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const onSubmit = useCallback(() => {
    trackEvent('sidebar_migration_confirm');
    setIsLoading(true);
  }, []);

  const onClose = useCallback(() => {
    trackEvent('sidebar_migration_cancel');
    closeDialog();
  }, []);

  const onDone = useCallback(() => {
    trackEvent('sidebar_migration_done');
    closeDialog();
  }, []);

  useEffect(() => {
    setProjectsToProcess(uniqueKeys);
  }, [uniqueKeys]);

  useEffect(() => {
    const setProjectLevelSidebar = async () => {
      if (isLoading) {
        const [key, ...remaining] = projectsToProcess;

        setProgress((projectsProcessed.length + 1) / (projectsProcessed.length + projectsToProcess.length));

        const sucess = await setProjectLevelSidebarVisibilityWithRetry(key, 3);

        if (!sucess) setProjectsFailedToProcess([...projectsFailedToProcess, key]);

        setProjectsProcessed([...projectsProcessed, key]);
        setProjectsToProcess(remaining);

        if (remaining.length === 0) {
          setProgress(1);
          setIsLoading(false);
          setIsCompleted(true);
          await setSidebarVisibleByDefault(false);
        }
      }
    };
    setProjectLevelSidebar();
  }, [isLoading, projectsFailedToProcess, projectsProcessed, projectsToProcess]);

  return (
    <div data-cy="sidebar-migration-module">
      {projectsFailedToProcess.length > 0 && isCompleted
      && <SectionMessage title={`We couldn't make the sidebar visible for ${projectsFailedToProcess.length} projects`} appearance="warning">
        <p>To change the visibility of the sidebar please go to the project settings page</p>
        <p><strong>Project(s):</strong></p>
        <p>
          {projectsFailedToProcess.map((p, index) => {
            const separator = index === projectsFailedToProcess.length - 1 ? '' : ', ';
            return (
              <span key={p}>
                <a href={`${browseBaseUrl}/plugins/servlet/pagerduty/settings/project?project=${p}`} target="_blank" rel="noreferrer">
                  {p}
                </a>
                {separator}
              </span>
            );
          })}
        </p>
      </SectionMessage>}
      {isLoading && <ShowProgress progress={progress} toProcess={projectsToProcess.length} processed={projectsProcessed.length} projectsFailedToProcess={projectsFailedToProcess} />}
      {!isLoading && !isCompleted
        && <span>
          <p>
            This action applies to projects connected with rules to PagerDuty.<br />
            This action is a one-time occurrence following the Jira Plugin upgrade.
          </p>
          <p>
            Please keep this window open as this action may require some time.
            <br/>
            <strong>{projectsToProcess.length} project(s) will be updated.</strong></p>
        </span>
      }
      {!isLoading && isCompleted && projectsProcessed.length - projectsFailedToProcess.length > 0
      && <>
        <h5>PagerDuty sidebar default visibility was successfully changed</h5>
        <p>Sidebar visibility was changed for {projectsProcessed.length} project(s) </p>
      </>}
      <ModalButtons>
        {!isCompleted
      && <>
        <Button appearance="subtle" isDisabled={isLoading} onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton appearance="primary" isDisabled={isLoading} onClick={onSubmit} autoFocus isLoading={isLoading}>
          Confirm
        </LoadingButton>
      </>}
        {isCompleted
      && <>
        <Button appearance="primary" onClick={onDone}>
          Close
        </Button>
      </>}
      </ModalButtons>
    </div>
  );
};

const SidebarVisibilityMigrationModal = () => {
  const { rules } = useAllRules();
  const [browseBaseUrl, setBrowserUrl] = useState();

  useEffect(() => {
    async function getJiraContext() {
      const { browseBaseUrl } = await jira.getContext();
      setBrowserUrl(browseBaseUrl);
    }
    getJiraContext();
  }, []);

  const projects = rules.map(r => r?.config?.projects).concat().flat();
  const uniqueKeys = [...new Set(projects.map(p => p?.key))];

  return (
    <ModalContainer title="Change PagerDuty sidebar visibility for connected projects">
      <DataPresentation uniqueKeys={uniqueKeys} browseBaseUrl={browseBaseUrl} />
    </ModalContainer>
  );
};

export default SidebarVisibilityMigrationModal;
